import { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X, Copy, Check } from 'lucide-react';
import { log } from '../lib/logger';

interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  variant?: 'success' | 'error' | 'info' | 'warning';
  confirmText?: string;
  copyableText?: string;
  copyLabel?: string;
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  confirmText = 'OK',
  copyableText,
  copyLabel,
}: AlertDialogProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    buttonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  if (!isOpen) return null;

  const variantConfig = {
    success: {
      icon: CheckCircle,
      iconBg: 'bg-gradient-to-br from-emerald-100 to-emerald-50',
      iconColor: 'text-emerald-600',
      button: 'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 focus:ring-green-500 shadow-lg shadow-emerald-500/30',
      accentBorder: 'border-emerald-200',
    },
    error: {
      icon: XCircle,
      iconBg: 'bg-gradient-to-br from-red-100 to-red-50',
      iconColor: 'text-red-600',
      button: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:ring-red-500 shadow-lg shadow-red-500/30',
      accentBorder: 'border-red-200',
    },
    info: {
      icon: Info,
      iconBg: 'bg-gradient-to-br from-blue-100 to-blue-50',
      iconColor: 'text-blue-600',
      button: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-green-500 shadow-lg shadow-blue-500/30',
      accentBorder: 'border-blue-200',
    },
    warning: {
      icon: AlertTriangle,
      iconBg: 'bg-gradient-to-br from-amber-100 to-amber-50',
      iconColor: 'text-amber-600',
      button: 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 focus:ring-amber-500 shadow-lg shadow-amber-500/30',
      accentBorder: 'border-amber-200',
    },
  };

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleCopy = async () => {
    if (copyableText) {
      try {
        await navigator.clipboard.writeText(copyableText);
        setCopied(true);
      } catch (err) {
        log.error('Failed to copy:', err);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm backdrop-blur-md animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alert-title"
      aria-describedby="alert-description"
    >
      <div className="bg-white/95 backdrop-blur-lg rounded-xl shadow-xl max-w-md w-full animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 border border-white/50">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className={`flex-shrink-0 p-2 rounded-2xl ${config.iconBg} ${config.iconColor} shadow-sm`}>
              <IconComponent className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 id="alert-title" className="text-xl font-semibold text-gray-900 mb-2 leading-heading">
                {title}
              </h2>
              <p id="alert-description" className="text-gray-600 leading-reading whitespace-pre-line max-w-reading">
                {message}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors rounded-lg p-1 hover:bg-gray-100"
              aria-label="Close dialog"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {copyableText && (
            <div className={`mb-5 p-3 rounded-xl border-2 ${config.accentBorder} bg-gray-50`}>
              {copyLabel && (
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 leading-small">
                  {copyLabel}
                </p>
              )}
              <div className="flex items-center gap-3">
                <code className="flex-1 font-mono text-sm text-gray-900 break-all select-all leading-body">
                  {copyableText}
                </code>
                <button
                  onClick={handleCopy}
                  className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                    copied
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              {copied && (
                <p className="text-xs text-emerald-600 font-medium mt-2 animate-in fade-in slide-in-from-bottom-1 duration-200 leading-small">
                  Copied to clipboard!
                </p>
              )}
            </div>
          )}

          <button
            ref={buttonRef}
            onClick={onClose}
            className={`w-full px-5 py-3 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all leading-body ${config.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
